import React from "react";
import { Input } from "reactstrap";
import ColumnHeader from "./components/ColumnHeader";
import { formatCurrency, formatNumber, truncateWithDots } from "./formatters";
import type { ControlStockRow } from "./types";

const ARTICLE_COLUMN_WIDTH = 230;
const UM_COLUMN_WIDTH = 35;

export const buildFamilyLineColumns = () => [
  {
    header: "FAMILIA",
    accessorKey: "familia",
    enableColumnFilter: false,
  },
  {
    header: "LÍNEA",
    accessorKey: "linea",
    enableColumnFilter: false,
  },
  {
    header: "INICIAL",
    accessorKey: "inventarioInicial",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "COMPRA",
    accessorKey: "compras",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "REM(+)",
    accessorKey: "remisionPositiva",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "REM(-)",
    accessorKey: "remisionNegativa",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "VENTA",
    accessorKey: "ventas",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "MERMA",
    accessorKey: "ajusteNegativo",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "TEORICO",
    accessorKey: "teorico",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "FINAL",
    accessorKey: "inventarioFinal",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "DIF.",
    accessorKey: "diferencia",
    enableColumnFilter: false,
    cell: (cell: any) => {
      const value = Number(cell.getValue() ?? 0);
      const color =
        value === 0 ? "text-muted" : value > 0 ? "text-success" : "text-danger";
      return <span className={`fw-semibold ${color}`}>{formatCurrency(value)}</span>;
    },
  },
];

export const buildBranchColumns = () => [
  {
    header: "SUCURSAL",
    accessorKey: "sucursal",
    enableColumnFilter: false,
  },
  {
    header: "INICIAL",
    accessorKey: "inventarioInicial",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "COMPRA",
    accessorKey: "compras",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "REM(+)",
    accessorKey: "remisionPositiva",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "REM(-)",
    accessorKey: "remisionNegativa",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "VENTA",
    accessorKey: "ventas",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "MERMA",
    accessorKey: "ajusteNegativo",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "TEORICO",
    accessorKey: "teorico",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "FINAL",
    accessorKey: "inventarioFinal",
    enableColumnFilter: false,
    cell: (cell: any) => formatCurrency(Number(cell.getValue() ?? 0)),
  },
  {
    header: "DIF.",
    accessorKey: "diferencia",
    enableColumnFilter: false,
    cell: (cell: any) => {
      const value = Number(cell.getValue() ?? 0);
      const color =
        value === 0 ? "text-muted" : value > 0 ? "text-success" : "text-danger";
      return <span className={`fw-semibold ${color}`}>{formatCurrency(value)}</span>;
    },
  },
];

type StockColumnsParams = {
  previousDateLabel: string | undefined;
  movementDateLabel: string;
  finalDateLabel: string | undefined;
  columnHasData: Record<string, boolean>;
  inventoryHeaderMovementIds: { inventarioInicial: string[]; inventarioFinal: string[] };
  inventoryMovementFilter: { initial: string; final: string };
  onInventoryMovementFilterChange: (type: "initial" | "final", value: string) => void;
  metricMode: "cantidad" | "monto" | "ambos";
  getAmountValue: (row: ControlStockRow, field: keyof ControlStockRow) => number;
  renderMetricCell: (
    row: ControlStockRow,
    field: keyof ControlStockRow,
    options?: { signed?: boolean; className?: string },
  ) => React.ReactNode;
  onRowViewMovements: (row: ControlStockRow) => void;
};

export const buildStockColumns = (params: StockColumnsParams) => {
  const {
    previousDateLabel,
    movementDateLabel,
    finalDateLabel,
    columnHasData,
    inventoryHeaderMovementIds,
    inventoryMovementFilter,
    onInventoryMovementFilterChange,
    metricMode,
    getAmountValue,
    renderMetricCell,
    onRowViewMovements,
  } = params;

  const compareNumbers = (a: number, b: number) =>
    a === b ? 0 : a > b ? 1 : -1;
  const compareStrings = (a: string, b: string) => a.localeCompare(b, "es");
  const placeTotalsLast = (rowA: any, rowB: any) => {
    if (rowA.original?.__isGrandTotal) return 1;
    if (rowB.original?.__isGrandTotal) return -1;
    return 0;
  };
  const metricAccessor =
    (field: keyof ControlStockRow) => (row: ControlStockRow) => {
      if (metricMode === "cantidad") {
        return Number(row[field] ?? 0);
      }
      return getAmountValue(row, field);
    };
  const metricSortingFn = (rowA: any, rowB: any, columnId: string) => {
    const totalOrder = placeTotalsLast(rowA, rowB);
    if (totalOrder !== 0) return totalOrder;
    return compareNumbers(
      Number(rowA.getValue(columnId) ?? 0),
      Number(rowB.getValue(columnId) ?? 0),
    );
  };
  const buildInventoryHeader = (
    type: "initial" | "final",
    title: string,
    dateLabel: string | undefined,
    ids: string[],
  ) => {
    const hasData =
      type === "initial"
        ? columnHasData.inventarioInicial
        : columnHasData.inventarioFinal;
    return (
      <div className="d-flex flex-column lh-1">
        <span>{title}</span>
        {hasData && dateLabel ? (
          <small className="text-muted fw-normal mt-1 d-inline-block">
            {dateLabel}
          </small>
        ) : null}
      </div>
    );
  };

  return [
    {
      header: () => <div style={{ width: ARTICLE_COLUMN_WIDTH }}>ARTICULO</div>,
      accessorKey: "articulo",
      enableColumnFilter: false,
      sortingFn: (rowA: any, rowB: any) => {
        const totalOrder = placeTotalsLast(rowA, rowB);
        if (totalOrder !== 0) return totalOrder;
        return compareStrings(
          String(rowA.original?.articulo ?? ""),
          String(rowB.original?.articulo ?? ""),
        );
      },
      cell: (cell: any) => {
        const row = cell.row.original;
        if (row.__isCollectionHeader) {
          return (
            <div className="d-flex justify-content-end align-items-center w-100">
              <span className="fw-semibold text-uppercase text-end">
                {row.__collectionLabel ?? "Sin colección"}
              </span>
            </div>
          );
        }
        if (row.__isTotal) {
          return (
            <span className="text-muted text-uppercase small">
              {row.__totalLabel ?? "Totales (PYG)"}
            </span>
          );
        }
        const articulo = String(cell.getValue() ?? "");
        const idArticulo = String(row.idArticulo ?? "").trim();
        const label = articulo || idArticulo || "Sin artículo";
        return (
          <div style={{ width: ARTICLE_COLUMN_WIDTH }}>
            <div className="d-inline-flex align-items-center gap-1 text-nowrap">
              {idArticulo ? <span className="badge bg-primary">{idArticulo}</span> : null}
              <span className="fw-semibold" title={label}>
                {truncateWithDots(label, 28)}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      header: "COSTO",
      accessorKey: "valorUnitario",
      enableColumnFilter: false,
      sortingFn: (rowA: any, rowB: any) => {
        const totalOrder = placeTotalsLast(rowA, rowB);
        if (totalOrder !== 0) return totalOrder;
        return compareNumbers(
          Number(rowA.original?.valorUnitario ?? 0),
          Number(rowB.original?.valorUnitario ?? 0),
        );
      },
      cell: (cell: any) => {
        const row = cell.row.original;
        if (row.__isCollectionHeader || row.__isTotal) {
          return <span className="text-muted">-</span>;
        }
        return <span>{formatCurrency(row.valorUnitario)}</span>;
      },
    },
    {
      header: () => <div style={{ width: UM_COLUMN_WIDTH }}>U/M</div>,
      accessorKey: "unidadMedida",
      enableColumnFilter: false,
      sortingFn: (rowA: any, rowB: any) => {
        const totalOrder = placeTotalsLast(rowA, rowB);
        if (totalOrder !== 0) return totalOrder;
        return compareStrings(
          String(rowA.original?.unidadMedida ?? ""),
          String(rowB.original?.unidadMedida ?? ""),
        );
      },
      cell: (cell: any) => {
        const value = String(cell.getValue() ?? "");
        return (
          <div
            className="text-truncate"
            style={{ width: UM_COLUMN_WIDTH }}
            title={value}
          >
            {value}
          </div>
        );
      },
    },
    {
      header: buildInventoryHeader(
        "initial",
        "INICIAL",
        columnHasData.inventarioInicial ? previousDateLabel || "" : undefined,
        inventoryHeaderMovementIds.inventarioInicial,
      ),
      id: "inventarioInicial",
      accessorFn: metricAccessor("inventarioInicial"),
      enableColumnFilter: false,
      sortingFn: metricSortingFn,
      cell: (cell: any) => renderMetricCell(cell.row.original, "inventarioInicial"),
    },
    {
      header: (
        <ColumnHeader
          title="COMPRA"
          subtitle={columnHasData.compras ? movementDateLabel || undefined : undefined}
        />
      ),
      id: "compras",
      accessorFn: metricAccessor("compras"),
      enableColumnFilter: false,
      sortingFn: metricSortingFn,
      cell: (cell: any) => renderMetricCell(cell.row.original, "compras"),
    },
    {
      header: (
        <ColumnHeader
          title="REM(+)"
          subtitle={
            columnHasData.remisionPositiva ? movementDateLabel || undefined : undefined
          }
        />
      ),
      id: "remisionPositiva",
      accessorFn: metricAccessor("remisionPositiva"),
      enableColumnFilter: false,
      sortingFn: metricSortingFn,
      cell: (cell: any) => renderMetricCell(cell.row.original, "remisionPositiva"),
    },
    {
      header: (
        <ColumnHeader
          title="REM(-)"
          subtitle={
            columnHasData.remisionNegativa ? movementDateLabel || undefined : undefined
          }
        />
      ),
      id: "remisionNegativa",
      accessorFn: metricAccessor("remisionNegativa"),
      enableColumnFilter: false,
      sortingFn: metricSortingFn,
      cell: (cell: any) => renderMetricCell(cell.row.original, "remisionNegativa"),
    },
    {
      header: (
        <ColumnHeader
          title="VENTA"
          subtitle={columnHasData.ventas ? movementDateLabel || undefined : undefined}
        />
      ),
      id: "ventas",
      accessorFn: metricAccessor("ventas"),
      enableColumnFilter: false,
      sortingFn: metricSortingFn,
      cell: (cell: any) => renderMetricCell(cell.row.original, "ventas"),
    },
    {
      header: (
        <ColumnHeader
          title="MERMA"
          subtitle={
            columnHasData.ajusteNegativo ? movementDateLabel || undefined : undefined
          }
        />
      ),
      id: "ajusteNegativo",
      accessorFn: metricAccessor("ajusteNegativo"),
      enableColumnFilter: false,
      sortingFn: metricSortingFn,
      cell: (cell: any) => renderMetricCell(cell.row.original, "ajusteNegativo"),
    },
    {
      header: "TEORICO",
      id: "teorico",
      accessorFn: metricAccessor("teorico"),
      enableColumnFilter: false,
      sortingFn: metricSortingFn,
      cell: (cell: any) =>
        renderMetricCell(cell.row.original, "teorico", {
          className: "fw-semibold text-primary",
        }),
    },
    {
      header: buildInventoryHeader(
        "final",
        "FINAL",
        columnHasData.inventarioFinal ? finalDateLabel || "" : undefined,
        inventoryHeaderMovementIds.inventarioFinal,
      ),
      id: "inventarioFinal",
      accessorFn: metricAccessor("inventarioFinal"),
      enableColumnFilter: false,
      sortingFn: metricSortingFn,
      cell: (cell: any) =>
        renderMetricCell(cell.row.original, "inventarioFinal", {
          className: "fw-semibold",
        }),
    },
    {
      header: "DIF.",
      id: "diferencia",
      accessorFn: metricAccessor("diferencia"),
      enableColumnFilter: false,
      sortingFn: metricSortingFn,
      cell: (cell: any) => {
        const value = cell.row.original.diferencia as number;
        const color =
          value === 0 ? "text-muted" : value > 0 ? "text-success" : "text-danger";
        return renderMetricCell(cell.row.original, "diferencia", {
          signed: true,
          className: "fw-semibold " + color,
        });
      },
    },
    {
      header: <span className="d-block text-center">ACC.</span>,
      id: "acciones",
      enableColumnFilter: false,
      cell: (cell: any) => {
        const row = cell.row.original as ControlStockRow;
        if (
          row.__isCollectionHeader ||
          row.__isTotal ||
          row.__isCollectionSubtotal ||
          row.__isGrandTotal
        ) {
          return <span className="text-muted">-</span>;
        }
        return (
          <div className="d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-sm btn-soft-primary"
              title="Ver movimientos"
              onClick={() => onRowViewMovements(row)}
            >
              <i className="ri-search-eye-line"></i>
            </button>
          </div>
        );
      },
    },
  ];
};
